import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { MostRecentDataChart } from '../../../../components/Charts/Leaderboard/MostRecentRounds/MostRecentDataChart';
import { IInvestmentDataModel } from '../../../../data-models/investment.data-model';
import { IMetricsDataModel } from '../../../../data-models/metrics.data-model';
import { useSelectedCurrency } from '../../../../hooks/useSelectedCurrency';
import { CHART_COMPONENTS } from '../../../../types';
import { useMostRecentParticipatingRounds } from '../../hooks/useMostRecentParticipatingRounds';
import { selectedViewTemporalDatePFJ } from '../../state/PageStateJ';
import { roundTrackerMetricsByDateAtom } from '../../state/RoundTrackerMetricsStateJ';
import { IPortfolioChartProps } from './ChartComponent';

// Mirror the API's round-tracker gcAmount: only new-money cost counts as an investment.
// Conversions (Restructure In/Out) attach to the round but are not new capital, so exclude them.
const COST_TRANSACTION_TYPES = new Set(['Investment - Cost', 'Investment - Special Contribution']);

// FD-3701: the round column is labelled "Round" here (renamed from "Stage").
export const MOST_RECENT_INVESTMENTS_COLUMN_HEADERS = [
  'Company',
  'Deal Lead',
  'Round',
  'Date',
  'Investment Amount',
];

export function MostRecentInvestmentsChart(
  props: Pick<
    IPortfolioChartProps<IMetricsDataModel>,
    'filteredMetrics' | 'isFullScreen' | 'onOpenFullScreen'
  >
) {
  const date = useAtomValue(selectedViewTemporalDatePFJ);
  const { selectedCurrency } = useSelectedCurrency();
  const { data: roundTrackerMetrics } = useAtomValue(
    roundTrackerMetricsByDateAtom({ date, currency: selectedCurrency })
  );
  const filteredData = useMemo(
    () => getFilteredRoundTrackerData(roundTrackerMetrics || [], props.filteredMetrics),
    [roundTrackerMetrics, props.filteredMetrics]
  );
  const data = useMostRecentParticipatingRounds(filteredData);

  return (
    <MostRecentDataChart
      id={CHART_COMPONENTS.MOST_RECENT_PARTICIPATING_ROUNDS}
      width={640}
      data={data}
      columnHeaders={MOST_RECENT_INVESTMENTS_COLUMN_HEADERS}
      columnFields={['company', 'dealLead', 'round', 'date', 'formattedValue']}
      loading={false}
      isFullScreen={props.isFullScreen}
      onOpenFullScreen={props.onOpenFullScreen}
    />
  );
}

// The chart is shown in the Portfolio Overview page, where filters are applied to metrics data;
// Most recent investments come from round tracker, but we need to recalculate amount so we include only filtered funds and companies
export function getFilteredRoundTrackerData(
  data: IInvestmentDataModel[],
  filteredMetrics: IMetricsDataModel[]
) {
  if (!data.length) {
    return [];
  }

  /*  if metrics are split by fund/fundType, we have multiple rows with same companyId, 
  but transactions are already filtered by fund/fundType */
  const filteredMetricsByCompanyIds = filteredMetrics.reduce((acc, metric) => {
    const existing = acc.get(metric.companyId);
    if (existing) {
      existing.push(metric);
    } else {
      acc.set(metric.companyId, [metric]);
    }
    return acc;
  }, new Map<number, IMetricsDataModel[]>());

  return data.reduce((acc, rt) => {
    const transactions = filteredMetricsByCompanyIds
      .get(rt.companyId)
      ?.flatMap((metric) => metric.transactions);
    if (!transactions) {
      return acc;
    }

    const amount = transactions.reduce((acc, transaction) => {
      if (
        transaction.investmentRoundId != null &&
        transaction.investmentRoundId === rt.investmentRoundId &&
        COST_TRANSACTION_TYPES.has(transaction.transType)
      ) {
        return acc + transaction.amount;
      }
      return acc;
    }, 0);

    acc.push({ ...rt, gcAmount: Math.max(amount, 0) });
    return acc;
  }, [] as IInvestmentDataModel[]);
}
